import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { toast } from '@livable/saas-react-components';
import React, { useRef, useState } from 'react';
import { ConfirmActionModal } from 'src/pages/BillsPage/BillDetails/ConfirmActionModal';
import { useStore } from 'src/stores';
import { getRecoveredBillDeleteConfirmation, getRecoveredBillDeletedToast } from './BillAutomationExtraction/billAutomationExtractionUtils';
import { isOnUtility, refreshArchivedAllocation } from './archivedAllocationRefresh';

/**
 * Deleting an uploaded bill from apply billing (LB-5952), with its confirmation.
 *
 * The one way an allocation made from an uploaded bill gets a different bill: an allocation never outlives the bill it
 * was made from. Bill-automation archives the bill, and property-service archives the allocations made from it, so the
 * allocation is reloaded afterwards -- it no longer has a bill -- and the screen lets go of what it held. Used by the
 * header's delete, the file copy's delete and "Upload another bill", so all three behave as one.
 *
 * @param property the property the modal is open on
 * @returns `request(billId, { isAllocatedHere })` to ask for the delete, and the confirmation to render
 */
export function useUploadedBillDelete() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    property = _ref.property;
  const store = useStore();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    asked = _useState2[0],
    setAsked = _useState2[1];
  // The latest delete asked for here: an earlier one still counting must not open its dialog over it.
  const latestRequest = useRef(0);
  const getBillingTab = () => {
    var _store$homeDashboardS;
    return (_store$homeDashboardS = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.billingTab;
  };
  const request = async function (billId) {
    var _getBillingTab, _store$companyBills$c, _counted$response;
    let _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref2$isAllocatedHere = _ref2.isAllocatedHere,
      isAllocatedHere = _ref2$isAllocatedHere === void 0 ? false : _ref2$isAllocatedHere;
    if (!billId) return;
    latestRequest.current += 1;
    const thisRequest = latestRequest.current;

    // What the screen was on when the delete was asked for. The modal stays usable while it is in flight, and the
    // held state is shared by every utility, so it is only let go of if the screen is still on this one.
    const applyBill = ((_getBillingTab = getBillingTab()) === null || _getBillingTab === void 0 ? void 0 : _getBillingTab.applyBill) || {};
    const deletedFrom = {
      billingCycleId: applyBill.billingCycleId,
      propertyId: property === null || property === void 0 ? void 0 : property.id,
      utilityId: applyBill.utilityId
    };
    // Every allocation made from the bill goes with it, not only this table's, so the confirmation asks how many. Without
    // an answer it speaks for this table's alone, as it always has.
    const counted = await Promise.resolve((_store$companyBills$c = store.companyBills.countBillAllocationsAsync) === null || _store$companyBills$c === void 0 ? void 0 : _store$companyBills$c.run({
      billId
    })).catch(() => undefined);
    if (thisRequest !== latestRequest.current) return;
    setAsked({
      billId,
      deletedFrom,
      isAllocatedHere,
      allocationCount: counted === null || counted === void 0 || (_counted$response = counted.response) === null || _counted$response === void 0 ? void 0 : _counted$response.allocationCount
    });
  };
  const confirm = async () => {
    var _allocations$tempFile, _allocations$tempFile2;
    const deleting = asked;
    setAsked(null);
    if (!deleting) return;
    const deletedFrom = deleting.deletedFrom;
    const resp = await store.companyBills.deleteBillAsync.run({
      billId: deleting.billId
    });
    // No answer at all is a request that failed, and the request has said so itself.
    if (!resp) return;
    if (!resp.isSuccessful) {
      // The service refuses a bill billed through an allocation or pushed, and says which.
      toast('error', {
        title: 'Bill not deleted',
        body: [(resp === null || resp === void 0 ? void 0 : resp.message) || 'This bill can no longer be deleted.']
      });
      return;
    }
    toast('success', {
      body: [getRecoveredBillDeletedToast(deleting)]
    });

    // The allocation it was made from is archived with it. A screen closed for want of a reload has let go already.
    if (deleting.isAllocatedHere) {
      const refreshed = await refreshArchivedAllocation({
        store,
        property,
        from: deletedFrom
      });
      if (refreshed === 'closed') return;
    }

    // Only the deleted bill is let go of. Not a replacement started meanwhile, which holds no bill while it uploads,
    // and not one uploaded since -- nor a bill an allocation removal here let go of earlier (LB-6028), which would
    // otherwise be found for the cycle again.
    const allocations = property === null || property === void 0 ? void 0 : property.allocations;
    const heldBillId = allocations === null || allocations === void 0 || (_allocations$tempFile = allocations.tempFileData) === null || _allocations$tempFile === void 0 || (_allocations$tempFile = _allocations$tempFile.billAutomationResponse) === null || _allocations$tempFile === void 0 ? void 0 : _allocations$tempFile.billId;
    if (heldBillId === deleting.billId && !(allocations !== null && allocations !== void 0 && (_allocations$tempFile2 = allocations.tempFileData) !== null && _allocations$tempFile2 !== void 0 && _allocations$tempFile2.hasUnfinishedReplacement) && isOnUtility(getBillingTab(), deletedFrom)) {
      allocations.resetTempFileData({
        keepReleased: true
      });
    }
  };
  const confirmation = /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Delete bill",
    isOpen: !!asked,
    message: getRecoveredBillDeleteConfirmation({
      isAllocatedHere: !!(asked !== null && asked !== void 0 && asked.isAllocatedHere),
      allocationCount: asked === null || asked === void 0 ? void 0 : asked.allocationCount
    }),
    onClose: () => setAsked(null),
    onConfirm: confirm,
    title: "Delete bill?"
  });
  return {
    request,
    confirmation
  };
}